import { useState, useCallback } from 'react';
import { api, Project, Dispute, DisputeDetail, Evidence } from '../services/api';

interface UseProjectsReturn {
  projects: Project[];
  loading: boolean;
  error: string | null;
  fetchProjects: (walletAddress?: string) => Promise<Project[]>;
  fetchProject: (id: string) => Promise<Project | null>;
  createProject: (projectData: {
    title: string;
    description?: string;
    client: string;
    provider: string;
    totalAmount: string;
    milestones: Array<{ amount: string; description: string }>;
    contractAddress?: string;
    projectId?: number;
  }) => Promise<Project>;
  approveMilestone: (
    projectId: string,
    milestoneId: number,
    walletAddress: string,
    isClient: boolean
  ) => Promise<void>;
  releaseMilestone: (projectId: string, milestoneId: number) => Promise<{ project: Project; message: string }>;
  fetchDemoProjects: () => Promise<Project[]>;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  openDispute: (projectId: string, milestoneIndex: number, reason: string, txHash: string, initiatorAddress?: string) => Promise<void>;
  fetchDisputes: (projectId: string, status?: string) => Promise<Dispute[]>;
  fetchDisputeDetail: (projectId: string, milestoneIndex: number) => Promise<DisputeDetail | null>;
  setArbitrator: (projectId: string, arbitratorAddress: string, txHash: string) => Promise<void>;
  resolveDispute: (
    projectId: string,
    milestoneIndex: number,
    clientSharePercent: number,
    providerSharePercent: number,
    txHash: string
  ) => Promise<void>;
  submitEvidence: (
    projectId: string,
    milestoneIndex: number,
    evidenceData: {
      evidenceType: 'text' | 'document';
      evidenceText?: string;
      fileName?: string;
      fileSize?: number;
      fileType?: string;
      fileData?: string;
      submissionNotes?: string;
      submittedBy: string;
    }
  ) => Promise<void>;
  getEvidence: (projectId: string, milestoneIndex: number) => Promise<Evidence[]>;
}

export const useProjects = (): UseProjectsReturn => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async (walletAddress?: string): Promise<Project[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getProjects(walletAddress);
      // Don't set projects here - let DashboardPage manage its own state
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProject = useCallback(async (id: string): Promise<Project | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getProject(id);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch project');
      console.error('Error fetching project:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createProject = useCallback(async (projectData: {
    title: string;
    description?: string;
    client: string;
    provider: string;
    totalAmount: string;
    milestones: Array<{ amount: string; description: string }>;
    contractAddress?: string;
    projectId?: number;
  }): Promise<Project> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.createProject(projectData);
      setProjects((prev) => [...prev, data]);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveMilestone = useCallback(async (
    projectId: string,
    milestoneId: number,
    walletAddress: string,
    isClient: boolean
  ) => {
    setLoading(true);
    setError(null);
    try {
      const updatedProject = await api.approveMilestone(projectId, milestoneId, walletAddress, isClient);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? updatedProject.project : p))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to approve milestone');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const releaseMilestone = useCallback(async (projectId: string, milestoneId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.releaseMilestone(projectId, milestoneId);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? response.project : p))
      );
      return response;
    } catch (err: any) {
      setError(err.message || 'Failed to release milestone');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDemoProjects = useCallback(async (): Promise<Project[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDemoProjects();
      // Don't set projects here - let DashboardPage manage its own state
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch demo projects');
      console.error('Error fetching demo projects:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const openDispute = useCallback(async (projectId: string, milestoneIndex: number, reason: string, txHash: string, initiatorAddress?: string) => {
    setLoading(true);
    setError(null);
    try {
      await api.openDispute(projectId, milestoneIndex, reason, txHash, initiatorAddress);
    } catch (err: any) {
      setError(err.message || 'Failed to open dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisputes = useCallback(async (projectId: string, status?: string): Promise<Dispute[]> => {
    setLoading(true);
    setError(null);
    try {
      const disputes = await api.getDisputes(projectId, status);
      return disputes;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch disputes');
      console.error('Error fetching disputes:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisputeDetail = useCallback(async (projectId: string, milestoneIndex: number): Promise<DisputeDetail | null> => {
    setLoading(true);
    setError(null);
    try {
      const dispute = await api.getDisputeDetail(projectId, milestoneIndex);
      return dispute;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dispute details');
      console.error('Error fetching dispute details:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setArbitrator = useCallback(async (projectId: string, arbitratorAddress: string, txHash: string) => {
    setLoading(true);
    setError(null);
    try {
      await api.setArbitrator(projectId, arbitratorAddress, txHash);
    } catch (err: any) {
      setError(err.message || 'Failed to set arbitrator');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resolveDispute = useCallback(async (
    projectId: string,
    milestoneIndex: number,
    clientSharePercent: number,
    providerSharePercent: number,
    txHash: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      await api.resolveDispute(projectId, milestoneIndex, clientSharePercent, providerSharePercent, txHash);
    } catch (err: any) {
      setError(err.message || 'Failed to resolve dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitEvidence = useCallback(async (
    projectId: string,
    milestoneIndex: number,
    evidenceData: {
      evidenceType: 'text' | 'document';
      evidenceText?: string;
      fileName?: string;
      fileSize?: number;
      fileType?: string;
      fileData?: string;
      submissionNotes?: string;
      submittedBy: string;
    }
  ) => {
    try {
      await api.submitEvidence(projectId, milestoneIndex, evidenceData);
    } catch (err: any) {
      setError(err.message || 'Failed to submit evidence');
      throw err;
    }
  }, []);

  const getEvidence = useCallback(async (projectId: string, milestoneIndex: number): Promise<Evidence[]> => {
    try {
      return await api.getEvidence(projectId, milestoneIndex);
    } catch (err: any) {
      console.error('Error fetching evidence:', err);
      return [];
    }
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    fetchProject,
    createProject,
    approveMilestone,
    releaseMilestone,
    fetchDemoProjects,
    setProjects,
    openDispute,
    fetchDisputes,
    fetchDisputeDetail,
    setArbitrator,
    resolveDispute,
    submitEvidence,
    getEvidence,
  };
};

